import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ExamSessionFormDialog from "@/components/ExamSessionFormDialog";
import { Plus, Award, Calendar, MapPin, Users, ArrowRight, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { useAuth, isStudent, isAdminOrSegreteria } from "@/lib/auth";

const STATUS_STYLE = {
  aperto: "bg-emerald-50 text-emerald-800 border-emerald-200",
  chiuso: "bg-amber-50 text-amber-800 border-amber-200",
  svolto: "bg-stone-100 text-stone-700 border-stone-200",
};

export default function Exams() {
  const { user } = useAuth();
  const student = isStudent(user);
  const canManage = isAdminOrSegreteria(user);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [yearFilter, setYearFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState(student ? "aperto" : "all");
  const [ayFilter, setAyFilter] = useState("all"); // year_academic
  const [historicalFilter, setHistoricalFilter] = useState("all"); // all | only | exclude
  const [createOpen, setCreateOpen] = useState(false);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["exam-sessions", { yearFilter, statusFilter, ayFilter, historicalFilter, student }],
    queryFn: async () => {
      const params = {};
      if (yearFilter !== "all") params.year_level = yearFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (ayFilter !== "all") params.year_academic = ayFilter;
      if (historicalFilter === "only") params.is_historical = true;
      if (historicalFilter === "exclude") params.is_historical = false;
      if (student) params.upcoming_only = true;
      return (await api.get("/exams/sessions", { params })).data;
    },
  });
  const { data: stats } = useQuery({
    queryKey: ["exam-stats"],
    queryFn: async () => (await api.get("/exams/-stats/overview")).data,
    enabled: !student,
  });

  const createMut = useMutation({
    mutationFn: async (p) => (await api.post("/exams/sessions", p)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["exam-sessions"] });
      toast.success("Appello aperto");
      setCreateOpen(false);
      navigate(`/scuola/esami/${d.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const bookMut = useMutation({
    mutationFn: async (sid) => (await api.post("/exams/bookings", { session_id: sid })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["exam-sessions"] }); toast.success("Prenotazione confermata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const cancelMut = useMutation({
    mutationFn: async (bid) => (await api.delete(`/exams/bookings/${bid}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["exam-sessions"] }); toast.success("Prenotazione annullata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const basePath = student ? "/portale/appelli" : "/scuola/esami";

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title={student ? "Appelli d'esame" : "Esami — Appelli"}
        subtitle={student
          ? "Prenotati agli appelli aperti dalla segreteria. Puoi annullare fino alla scadenza."
          : "Apri gli appelli per ogni corso, gestisci le prenotazioni e verbalizza i voti."}
        actions={canManage && (
          <Button
            data-testid="exam-add-btn"
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
          >
            <Plus size={16} className="mr-1.5"/> Nuovo appello
          </Button>
        )}
      />

      {!student && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Award size={12}/> Appelli</div>
            <div className="text-2xl font-semibold mt-1">{stats.sessions}</div>
          </Card>
          <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Aperti</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-700">{stats.open}</div>
          </Card>
          <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Prenotazioni</div>
            <div className="text-2xl font-semibold mt-1">{stats.bookings}</div>
          </Card>
          <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Voti verbalizzati</div>
            <div className="text-2xl font-semibold mt-1">{stats.grades}</div>
          </Card>
        </div>
      )}

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-stone-100 flex-wrap">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-36" data-testid="exam-year-filter"><SelectValue placeholder="Anno"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli anni</SelectItem>
              <SelectItem value="1">I anno</SelectItem>
              <SelectItem value="2">II anno</SelectItem>
              <SelectItem value="3">III anno</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="exam-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="aperto">Aperti</SelectItem>
              <SelectItem value="chiuso">Chiusi</SelectItem>
              <SelectItem value="svolto">Svolti</SelectItem>
            </SelectContent>
          </Select>
          {!student && (
            <>
              <Select value={ayFilter} onValueChange={setAyFilter}>
                <SelectTrigger className="w-44" data-testid="exam-ay-filter"><SelectValue placeholder="A.A."/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli A.A.</SelectItem>
                  <SelectItem value="2024/2025">2024/2025</SelectItem>
                  <SelectItem value="2025/2026">2025/2026</SelectItem>
                  <SelectItem value="2026/2027">2026/2027</SelectItem>
                </SelectContent>
              </Select>
              <Select value={historicalFilter} onValueChange={setHistoricalFilter}>
                <SelectTrigger className="w-44" data-testid="exam-historical-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli appelli</SelectItem>
                  <SelectItem value="only">Solo storico</SelectItem>
                  <SelectItem value="exclude">Escludi storico</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
        ) : sessions.length === 0 ? (
          <div className="py-16 text-center text-stone-400 text-sm">Nessun appello.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {sessions.map((s, i) => (
              <li
                key={s.id}
                data-testid={`exam-row-${s.id}`}
                className="p-4 md:p-5 flex items-start gap-4 hover:bg-stone-50 transition-colors animate-in-row"
                style={{ animationDelay: `${i * 15}ms` }}
              >
                <div className="w-14 h-14 rounded-lg grid place-items-center shrink-0 text-white"
                  style={{ background: s.area_color || "#1C352D" }}>
                  <div className="text-center">
                    <div className="text-[9px] uppercase tracking-widest opacity-70">{new Date(s.date).toLocaleDateString("it-IT", { month: "short" })}</div>
                    <div className="text-lg font-semibold leading-tight">{new Date(s.date).getDate()}</div>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`${basePath}/${s.id}`} className="font-medium text-[#1C352D] hover:underline">
                      {s.course_name || "—"}
                    </Link>
                    <Badge className={`text-[10px] rounded-full border ${STATUS_STYLE[s.status]}`}>{s.status}</Badge>
                    {s.is_historical && <Badge className="bg-amber-100 text-amber-900 border border-amber-200 text-[10px] rounded-full" data-testid={`exam-historical-${s.id}`}>STORICO</Badge>}
                    {s.year_academic && <Badge className="bg-stone-100 text-stone-700 border border-stone-200 text-[10px] rounded-full mono">A.A. {s.year_academic}</Badge>}
                    {s.course_year_level && <Badge className="bg-stone-100 text-stone-700 text-[10px] rounded-full">{s.course_year_level}° anno</Badge>}
                    {s.course_cfu && <Badge className="bg-[#E2725B] text-white text-[10px] rounded-full">{s.course_cfu} CFU</Badge>}
                  </div>
                  <div className="text-[12px] text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Calendar size={11}/> {fmtDate(s.date)}{s.time ? ` · ${s.time}` : ""}</span>
                    {s.room && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {s.room}</span>}
                    <span className="inline-flex items-center gap-1"><Users size={11}/> {s.bookings_count} prenotati · {s.grades_count} voti{s.max_students ? `/${s.max_students}` : ""}</span>
                    {s.examiners?.length > 0 && <span>· {s.examiners.join(", ")}</span>}
                  </div>
                </div>
                {student ? (
                  s.my_booking ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs border-red-300 text-red-700 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); cancelMut.mutate(s.my_booking.id); }}
                      data-testid={`exam-cancel-${s.id}`}
                    >Annulla prenotazione</Button>
                  ) : s.status === "aperto" ? (
                    <Button
                      size="sm"
                      className="rounded-full text-xs bg-[#E2725B] hover:bg-[#c85d47]"
                      onClick={(e) => { e.stopPropagation(); bookMut.mutate(s.id); }}
                      data-testid={`exam-book-${s.id}`}
                    ><BookOpen size={12} className="mr-1"/> Prenota</Button>
                  ) : null
                ) : (
                  <ArrowRight size={14} className="text-stone-400 self-center cursor-pointer" onClick={() => navigate(`${basePath}/${s.id}`)}/>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ExamSessionFormDialog
        open={createOpen} onOpenChange={setCreateOpen} mode="create"
        submitting={createMut.isPending}
        onSubmit={(p) => createMut.mutate(p)}
      />
    </div>
  );
}
